import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Sparkle, NotePencil, ChartLineUp, SignOut, ListBullets, List, X, HandHeart, GearSix, MagnifyingGlass } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import EntryDetailModal from "@/components/EntryDetailModal";
import NudgeBanner from "@/components/NudgeBanner";
import { TermsGateModal } from "@/components/TermsGateModal";

export default function AppShell() {
  const { user, logout, loading } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [entries, setEntries] = useState([]);
  const [refreshTick, setRefreshTick] = useState(0);
  const [selectedEntryId, setSelectedEntryId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState(null);

  useEffect(() => {
    if (!loading && !user) nav("/auth");
  }, [user, loading, nav]);

  useEffect(() => {
    if (!user) return;
    api.get("/entries").then((r) => setEntries(r.data)).catch(() => {});
  }, [user, refreshTick]);

  // Close drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Lock body scroll while drawer open on mobile
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  const handleEntryCreated = () => setRefreshTick((t) => t + 1);

  // Debounced backend search across entries (keyword, mood, or date like 2026-06)
  useEffect(() => {
    if (!user) return;
    const q = searchQ.trim();
    if (!q) {
      setSearchResults(null);
      return;
    }
    const t = setTimeout(() => {
      const isDate = /^\d{4}(-\d{2})?(-\d{2})?$/.test(q);
      const params = isDate ? { date_from: q, date_to: q.length === 4 ? `${q}-12-31` : q.length === 7 ? `${q}-31` : q } : { q };
      api.get("/entries", { params }).then((r) => setSearchResults(r.data)).catch(() => setSearchResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [searchQ, user, refreshTick]);

  const visibleEntries = searchResults !== null ? searchResults : entries;

  if (!user) return null;

  const renderSidebar = (variant) => {
    const sfx = `-${variant}`;
    return (
    <>
      <div className="p-5 lg:p-6 border-b border-line flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-clay/90 grid place-items-center">
            <Sparkle weight="fill" className="text-canvas" size={12} />
          </div>
          <span className="font-serif-display text-xl text-ink">Clarity Mirror</span>
        </Link>
        {variant === "mobile" && (
          <button
            onClick={() => setDrawerOpen(false)}
            className="lg:hidden text-ink-mute hover:text-ink p-1"
            data-testid="close-drawer"
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        )}
      </div>

      <nav className="p-4 space-y-1">
        <SidebarLink to="/app" end icon={<NotePencil size={18} weight="thin" />} label="Compose" testid={`nav-compose${sfx}`} />
        <SidebarLink to="/app/insights" icon={<ChartLineUp size={18} weight="thin" />} label="Insights" testid={`nav-insights${sfx}`} />
        <SidebarLink to="/app/subscribe" icon={<HandHeart size={18} weight="thin" />} label="Stay with it" testid={`nav-subscribe${sfx}`} />
        <SidebarLink to="/app/settings" icon={<GearSix size={18} weight="thin" />} label="Settings" testid={`nav-settings${sfx}`} />
      </nav>

      <div className="px-4 pt-2 pb-3 flex items-center gap-2 text-ink-mute">
        <ListBullets size={14} weight="thin" />
        <span className="text-xs font-semibold uppercase tracking-[0.2em]">Past entries</span>
      </div>
      <div className="px-4 pb-3">
        <div className="relative">
          <MagnifyingGlass size={14} weight="thin" className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-mute pointer-events-none" />
          <input
            type="text"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder="Search word, mood, or date…"
            data-testid={`entries-search${sfx}`}
            className="w-full bg-canvas border border-line rounded-full pl-8 pr-8 py-2 text-xs text-ink placeholder:text-ink-mute focus:border-clay focus:outline-none transition-colors"
          />
          {searchQ && (
            <button
              onClick={() => setSearchQ("")}
              data-testid={`entries-search-clear${sfx}`}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-mute hover:text-ink"
              aria-label="Clear search"
            >
              <X size={12} />
            </button>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-2" data-testid={`entries-list${sfx}`}>
        {visibleEntries.length === 0 ? (
          <p className="text-sm text-ink-mute italic px-2">
            {searchQ.trim() ? "Nothing matches that yet." : "No entries yet."}
          </p>
        ) : (
          visibleEntries.map((e) => (
            <button
              key={e.id}
              onClick={() => setSelectedEntryId(e.id)}
              data-testid={`entry-item-${e.id}${sfx}`}
              className="w-full text-left rounded-xl p-3 border border-line hover:border-clay hover:bg-canvas transition-colors cursor-pointer"
            >
              <div className="text-[10px] uppercase tracking-[0.2em] text-ink-mute mb-1">
                {new Date(e.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}{" "}
                ·{" "}
                {new Date(e.created_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
              </div>
              <p className="text-sm text-ink line-clamp-2 font-serif-display text-base">
                {e.content.slice(0, 90)}
              </p>
              {e.moods?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {e.moods.slice(0, 3).map((m) => (
                    <span key={m} className="text-[10px] px-2 py-0.5 rounded-full bg-canvas text-ink-soft border border-line">
                      {m}
                    </span>
                  ))}
                </div>
              )}
            </button>
          ))
        )}
      </div>

      <div className="p-4 border-t border-line">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded-full bg-sage/40 grid place-items-center text-ink text-sm font-medium shrink-0">
              {user.name?.[0]?.toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="text-sm text-ink truncate">{user.name}</p>
              <p className="text-xs text-ink-mute truncate">{user.email}</p>
            </div>
          </div>
          <button
            onClick={() => {
              logout();
              nav("/");
            }}
            data-testid={`logout-btn${sfx}`}
            className="text-ink-mute hover:text-ink transition-colors p-2 -mr-2"
            title="Sign out"
            aria-label="Sign out"
          >
            <SignOut size={18} weight="thin" />
          </button>
        </div>
      </div>
    </>
    );
  };

  return (
    <div className="min-h-screen lg:flex bg-canvas overflow-x-hidden" data-testid="app-shell">
      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 bg-canvas/90 backdrop-blur-md border-b border-line px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => setDrawerOpen(true)}
          className="p-2 -ml-2 text-ink"
          data-testid="open-drawer"
          aria-label="Open menu"
        >
          <List size={22} weight="thin" />
        </button>
        <Link to="/" className="flex items-center gap-2">
          <div className="w-5 h-5 rounded-full bg-clay/90 grid place-items-center">
            <Sparkle weight="fill" className="text-canvas" size={10} />
          </div>
          <span className="font-serif-display text-lg text-ink">Clarity Mirror</span>
        </Link>
        <div className="w-9" /> {/* spacer for symmetry */}
      </header>

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-72 shrink-0 border-r border-line bg-canvas-warm/60 flex-col">
        {renderSidebar("desktop")}
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-ink/40 backdrop-blur-sm"
          onClick={() => setDrawerOpen(false)}
          data-testid="drawer-backdrop"
        />
      )}
      <aside
        className={`lg:hidden fixed inset-y-0 left-0 z-50 w-[85%] max-w-xs bg-canvas-warm border-r border-line flex flex-col transition-transform duration-300 ease-out ${
          drawerOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="mobile-drawer"
        aria-hidden={!drawerOpen}
      >
        {renderSidebar("mobile")}
      </aside>

      <main className="flex-1 min-w-0 w-full lg:overflow-y-auto">
        <Outlet context={{ entries, onEntryCreated: handleEntryCreated }} />
      </main>

      <EntryDetailModal
        entryId={selectedEntryId}
        initialEntry={entries.find((e) => e.id === selectedEntryId)}
        onClose={() => setSelectedEntryId(null)}
        onChanged={handleEntryCreated}
      />

      <TermsGateModal />
    </div>
  );
}

function SidebarLink({ to, icon, label, testid, end }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testid}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
          isActive ? "bg-canvas text-ink border border-line" : "text-ink-soft hover:text-ink hover:bg-canvas/60"
        }`
      }
    >
      {icon}
      {label}
    </NavLink>
  );
}
